import { createSelector } from 'reselect'
import { 
  entitySelector,
  resultsSelector
} from 'containers/Entity/selectors'
import T from 'i18n-react'
import { create } from 'lodash'
import { 
  HEALTH_COLORS,
  rowSize, 
  VIEW_ALL,
  TRUNCATE,
  WEEKLY_UPDATE_FIELDS,
  SCHEDULE,
  SCHEDULE_REFORMAT
 } from './constants'
import * as R from 'ramda'
import P from 'helpers/predicates'
import _ from 'lodash'

const customViewsSelector = state => state.user.customViews

const changedListSelector = state => state.myProjects.postUpdated
const myProjectsListStateSelector = state => state.myProjects.projects
const selectedProjectsStateSelector = state => state.myProjects.selectedProjects
const changedProjectsStateSelector = state => state.myProjects.changedProjects
const currentFormChangesStateSelector = state => state.myProjects.currentFormChanges
const customColOrderStateSelector = state => state.myProjects.customColOrder
const searchFiltersStateSelector = state => state.myProjects.searchFilters
const listOrderStateSelector = state => state.myProjects.listOrder
const maxRowSizeStateSelector = state => state.myProjects.maxRowSize
const rowSizeStateSelector = state => state.myProjects.rowSize

const menuActiveStateSelector = state => state.myProjects.menuActive
const headerFiltersStateSelector = state => state.myProjects.headerFilters
const columnIndexStateSelector = state => state.myProjects.columnIndex
const sortTypeStateSelector = state => state.myProjects.sortType
const sortIndexStateSelector = state => state.myProjects.sortIndex
const sortByStateSelector = state => state.myProjects.sortBy
const inputHeaderSourceStateSelector = state => state.myProjects.inputHeaderSource

const fromStateSelector = state => state.project.search.from
const inputValueSelector = state => state.myProjects.inputValue

const popupActiveSelector = state => state.myProjects.popup
const popupPageSelector = state => state.myProjects.popupPage
const loadingActiveSelector = state => state.myProjects.loadingRender
const errorsSelector = state => state.myProjects.errors

const selectedViewSelector = state => state.myProjects.selectedView
const toggleNotifySelector = state => state.myProjects.toggleNotify

const filtersEntityStateSelector = state => {
  const filters = entitySelector(state)['project_filters']
  return filters ? filters : {}
}

const aeDetailsEntitySelector = createSelector(
  entitySelector,
  entities => entities.ae_details_selection
    ? entities.ae_details_selection
    : {}
)

const aeResourceLevelOptionsSelector = createSelector(
  aeDetailsEntitySelector,
  entity => {
    if (entity.ae_resource_levels) {
      let aeResourceLevel = entity.ae_resource_levels
      let toReturn = []
      Object.values(aeResourceLevel).forEach((aeResourceLevel) => {
        toReturn.push({ value: aeResourceLevel.level.toString(), label: aeResourceLevel.level.toString()})
      })
      return toReturn
    } else {
      return []
    }
  }
)

const projectHealthOptionsSelector = createSelector(
  aeDetailsEntitySelector,
  (entity) => {
    if(entity.project_healths){
      var projectHealths = entity.project_healths
      var toReturn = []
      projectHealths.forEach((projectHealth) => {
        toReturn.push({
          value: projectHealth.health, 
          label: projectHealth.health, 
          color: {
            backgroundColor: HEALTH_COLORS[projectHealth.health.toLowerCase()]
          } 
        })
      })
      return toReturn
    } else {
      return []
    }
  }
)

const projectWorkflowDetailsEntitySelector = createSelector(
    entitySelector,
    (entities) => entities.project_workflow_details
      ? entities.project_workflow_details
      : {}
  )
  
  const projectWorkflowDetailsSelector = createSelector(
    projectWorkflowDetailsEntitySelector,
    (entity) => {
      if(Object.values(entity)[0]){
        return Object.values(entity)[0]
      } else {
        return {}
      }
    }
  )
const projectWorkflowTransitionsSelector = createSelector(
    projectWorkflowDetailsSelector,
    (projectWorkflowDetails) => {
      if(projectWorkflowDetails.transitions && projectWorkflowDetails.transitions.length > 0){
        var toReturn = []
        projectWorkflowDetails.transitions.forEach((transition) => {
          toReturn.push({ value:  T.translate(`project.lifecycle.milestones.${transition}`), label: T.translate(`project.lifecycle.milestones.${transition}`)})
        })
        return toReturn
      } else {
        return []
      }
    }
  )

const myProjectsListSelector = createSelector(
    myProjectsListStateSelector,
    (list) => {
      let arr = Object.entries(list)
      if (arr.length > 0) {
          return arr.map((project, idx) => {
              return {
                  ...project[1],
                  id: idx,
                  project_id: project[0],
              }
          })
      } else {
          return []
      }
    }
)

const selectedProjectsFullInfoSelector = createSelector(
  selectedProjectsStateSelector,
  myProjectsListStateSelector,
  (selected, projects) => {
    return selected.map((id) => {
      return projects[id]
    })
  }
)

const headersSelector = createSelector(
  filtersEntityStateSelector,
  searchFiltersStateSelector,
  listOrderStateSelector,
  (filters, display, listOrder) => {
    return (R.compose(
    R.sort(R.comparator((
      { type: typeA, index: indexA, path: [pathA], count: countA },
      { type: typeB, index: indexB, path: [pathB], count: countB },
    ) => {
      let result = false
      // determines if the same object
      const isArray = countA && countB
      const isSamePath = listOrder[pathA] === listOrder[pathB]
      const isSameType = typeA === typeB
      const isSameIndex = indexA === indexB

      if (isArray && isSamePath) {
        if (isSameIndex) {
          result = listOrder[typeA] < listOrder[typeB]
        } else {
          result = indexA < indexB
        }
      } else {
        result = listOrder[typeA] < listOrder[typeB]
      }
      return result
    })),
    R.reduce((acc, { type, count }) => {
      let headers = []
      if (count) {
        let [start, end] = R.split('.', type)
        headers = R.times(n => ({
          index: n,
          type,
          count,
          path: end? [start, n, end] : [start, n]
        }))(count)
      } else {
        headers = [{ count, type, path: R.split('.', type) }]
      }
      return R.concat(acc, headers)
    })([]),
    R.filter(({ type }) => R.contains(type)(display)),
    R.filter(({ type }) => P.isNotNil(listOrder[type])),
    R.values
  )(filters))
  }
)

const listEntitySelector = createSelector(
  myProjectsListStateSelector,
  state => resultsSelector(state).projects,
  (entities, results) => {
    // console.log(entities)
    if (P.isNotNil(entities) && P.isNotNil(results))
      return R.map(R.flip(R.prop)(entities))(results)
    else
      return []
  }
)

const pathsSelector = createSelector(
  headersSelector,
  (headers) => {
    let current = R.map(R.prop('path'))(headers)
    return current
  }
)

const formattedHeadersSelector = createSelector(
  headersSelector,
  R.map(({ type, index }) => R.ifElse(
    R.isNil,
    R.always(T.translate(`search.type.${type}`)),
    R.always(T.translate(`search.type.${type}`) + ` ${index + 1}`)
  )(index))
)

const listSelector = createSelector(
  listEntitySelector,
  pathsSelector,
  formattedHeadersSelector,
  (lists, paths, headers) => {
    lists = lists.map(ls => {
      TRUNCATE.forEach(({label, value}) => {
        const str = _.get(ls, label)
        const toTruncate = (_.isString(str)) && (str.length > value)
        if (toTruncate) 
          _.set(ls, label, str.substring(0, value).concat('...'))
      })
      return ls
    })
    const table = R.compose(
      R.prepend(headers),
      R.map(row => {
        return R.map(path => {
            return (R.path(path, row))
        })(paths)
      }),
    )(lists)
    return table
  }
)

const rowSizeSelector = state => R.compose(
  R.append({ label: VIEW_ALL, value: VIEW_ALL }),
  R.map((value) => ({ label: value, value }))
)(rowSize)

const headerFilterValue = createSelector(
  headersSelector,
  headerFiltersStateSelector,
  (headers, filters) => R.map(R.compose(
    R.ifElse(
      P.isNotEmptyOrNil,
      R.always(true),
      R.always(false)
    ),
    R.flip(R.prop)(filters),
    R.prop('type')
  ))(headers)
)

const currentSortValue = createSelector(
  headersSelector,
  columnIndexStateSelector,
  sortTypeStateSelector,
  sortByStateSelector,
  (headers, index, sortType, sortBy) => {
    let type = R.path([index, 'type'])(headers)
    return R.ifElse(
      R.equals(sortType),
      R.always(sortBy),
      R.always(''),
    )(type)
  }
)

const currentSortType = createSelector(
  headersSelector,
  columnIndexStateSelector,
  (headers, index) => R.path([index, 'type'])(headers)
)

const currentHeaderFilterValue = createSelector(
  headersSelector,
  columnIndexStateSelector,
  headerFiltersStateSelector,
  (headers, index, filters) => {
    let type = R.path([index, 'type'])(headers)
    return R.compose(
      R.ifElse(
        R.isNil,
        R.always(''),
        R.identity
      ),
      R.prop(type)
    )(filters)
  }
)

const rowSizeValueSelector = createSelector(
  maxRowSizeStateSelector,
  rowSizeStateSelector,
  (max, value) => R.ifElse(
    R.equals(VIEW_ALL),
    R.always(max),
    R.always(value)
  )(value)
)

const sortEnabled = createSelector(
  headersSelector,
  columnIndexStateSelector,
  (headers, index) => {
    let count = R.path([index, 'count'])(headers)
    return R.ifElse(
      R.lt(1),
      R.F,
      R.T,
    )(count)
  }
)

const unformattedHeadersSelector = createSelector(
  headersSelector, 
  R.map(obj => obj.type)
)

const headerFiltersArraySelector = createSelector(
  headerFiltersStateSelector,
  R.compose(
    R.filter(R.propSatisfies(P.isNotEmptyOrNil, '1')),
    R.toPairs
  )
)

const queryFieldFiltersSelector = createSelector(
  headerFiltersArraySelector,
  R.map(([key, value]) => `${key}:[${value}]`)
)


const filtersAndQuerySelector = createSelector(
  headerFiltersStateSelector,
  (state) => {
    return Object.entries(state).map((entry) => {
      return {[entry[0]]: entry[1]}
    })
  }
)

const scheduleInfoForSelectedProjectSelector = createSelector(
  selectedProjectsStateSelector,
  myProjectsListSelector,
  (selected, projects) => {
    if (selected.length > 0 && projects.length > 0) {
      const currentProject = R.find(R.propEq('_id', selected[0].toString()), projects)
      return currentProject.schedule
    }
  }
)

const getActiveCustomViewSelector = createSelector(
  selectedViewSelector,
  customViewsSelector,
  (selectedView, customViews) => {
    return customViews.find((view) => view.value === selectedView)
  }
)

export {
    myProjectsListSelector,
    myProjectsListStateSelector,
    projectWorkflowTransitionsSelector,
    aeResourceLevelOptionsSelector,
    projectHealthOptionsSelector,
    selectedProjectsStateSelector,
    changedProjectsStateSelector,
    currentFormChangesStateSelector,
    selectedProjectsFullInfoSelector,
    rowSizeSelector,

    listSelector,
    menuActiveStateSelector,
    headerFilterValue,
    columnIndexStateSelector,
    currentSortValue,
    sortIndexStateSelector,
    sortByStateSelector,
    currentHeaderFilterValue,
    sortEnabled,
    sortTypeStateSelector,
    inputHeaderSourceStateSelector,
    currentSortType,
    unformattedHeadersSelector,
    listOrderStateSelector,
    fromStateSelector,
    rowSizeValueSelector,
    rowSizeStateSelector,
    queryFieldFiltersSelector,
    headersSelector,
    customColOrderStateSelector,
    inputValueSelector,
    filtersAndQuerySelector,
    popupActiveSelector,
    popupPageSelector,
    loadingActiveSelector,
    scheduleInfoForSelectedProjectSelector,
    errorsSelector,
    customViewsSelector,
    selectedViewSelector,
    getActiveCustomViewSelector,
    changedListSelector,
    toggleNotifySelector,
    searchFiltersStateSelector
  }